import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import * as prismic from '@prismicio/client'
import { PrismicRichText } from '@prismicio/react'
import classNames from 'classnames'
import dayjs from 'dayjs'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import Cookies from 'js-cookie'
import { TERMS_AND_CONDITIONS_BANNER_DOCUMENT_ID } from 'constants/cms'
import { consentCookie } from 'components/layout/Banners/CookiePolicy'
import { DATE_FORMAT } from 'constants/locale'
import { CMSCtx } from 'interfaces/cms'
import selectCmsCtx from 'redux/selectors/selectCmsCtx'
import { TermsAndConditionsBannerDocument } from 'types/prismic-types.generated'

interface TermsAndConditionsBannerProps {
  dismissed: boolean
  setDismissed: Dispatch<SetStateAction<boolean>>
  offset: boolean
}

export default function TermsAndConditionsBanner ({ dismissed, setDismissed, offset }: TermsAndConditionsBannerProps): JSX.Element | null {
  const cmsCtx: CMSCtx = useSelector(selectCmsCtx)
  const [banner, setBanner] = useState<TermsAndConditionsBannerDocument>()

  const fetchBanner = async (): Promise<void> => {
    try {
      const response = await cmsCtx.api.getSingle<TermsAndConditionsBannerDocument>(TERMS_AND_CONDITIONS_BANNER_DOCUMENT_ID,
        {
          predicates: [
            prismic.predicate.at(`my.${TERMS_AND_CONDITIONS_BANNER_DOCUMENT_ID}.visible`, true)
          ]
        }
      )
      const date = dayjs(response.last_publication_date).format(DATE_FORMAT)
      const consented = Cookies.get(`terms-and-conditions-${date}`)
      if (consented !== undefined) {
        return setDismissed(true)
      }
      // If user hasn't accepted the regular cookie they don't need to see the updated
      // terms banner
      const hasAcceptedCookiePolicy = Cookies.get(consentCookie) === 'true'
      if (!hasAcceptedCookiePolicy) {
        Cookies.set(`terms-and-conditions-${date}`, 'true', { expires: 999999 })
        return setDismissed(true)
      }
      setBanner(response)
    } catch (e) {
      console.log('service_unavailability_banner error', e)
    }
  }

  const handleClose = (): void => setDismissed(() => {
    if (banner !== undefined) {
      const date = dayjs(banner.last_publication_date).format(DATE_FORMAT)
      Cookies.set(`terms-and-conditions-${date}`, 'true', { expires: 999999 })
    }
    return true
  })

  useEffect(() => {
    if (cmsCtx !== null && cmsCtx !== undefined) {
      void fetchBanner()
    }
  }, [cmsCtx])

  if (banner === undefined) {
    return null
  }

  if (dismissed) {
    return null
  }

  return (
    <div
      data-testid='terms-and-conditions-banner'
      className={
        classNames(
          'w-80 w-90-m fixed z1055 layout-width-constraint bg-black white pa3 left-50 x--50',
          {
            bottom13: offset,
            bottom4: !offset
          }
        )
      }
    >
      <IconButton
        size='small'
        aria-label='close'
        className='absolute right0 pa4 white'
        onClick={handleClose}
      >
        <CloseIcon fontSize='small' />
      </IconButton>
      <PrismicRichText field={banner.data.content} />
    </div>
  )
}
